import { CommonModule } from '@angular/common';
import { Component, DestroyRef, input, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { filter, Subject } from 'rxjs';

import {
	SecNavButonData,
	SecondaryNavList,
	SecondaryNavService
} from '@shure/cloud/shared/services/secondary-nav-service';
import { PipesModule } from '@shure/cloud/shared/ui/pipes';
import {
	PrismButtonComponent,
	PrismIconComponent,
	PrismNavButtonComponent,
	PrismNavGroupComponent,
	PrismProgressIndicatorComponent
} from '@shure/prism-angular-components';

import { ToggleSecondayNavList } from '../../secondary-nav/secondary-nav.service';

@Component({
	selector: 'sh-secondary-nav',
	standalone: true,
	imports: [
		CommonModule,
		RouterModule,
		TranslocoModule,
		PipesModule,
		PrismIconComponent,
		PrismNavGroupComponent,
		PrismNavButtonComponent,
		PrismProgressIndicatorComponent,
		PrismButtonComponent
	],
	templateUrl: './secondary-nav.component.html',
	styleUrl: './secondary-nav.component.scss'
})
export class ShSecondaryNavComponent implements OnInit {
	public readonly navItems = input.required<SecondaryNavList[]>();
	public readonly navTitleTranslationKey = input.required<string>();
	public readonly hasSecondaryNavButton = input<boolean>(false);
	public readonly navButtonData = input<SecNavButonData>();

	public readonly routeAccount$: Subject<boolean> = new Subject<boolean>();
	public readonly toggleSecondayNavList = ToggleSecondayNavList;
	public readonly activeListItem = toSignal(this.secondaryNavService.getActiveListItem$());

	constructor(
		private readonly router: Router,
		private readonly secondaryNavService: SecondaryNavService,
		private readonly destroyRef: DestroyRef
	) {}

	public ngOnInit(): void {
		// Set initial active nav item and update it on navigation events
		const activeNavItem = this.secondaryNavService.getActiveListItemFromUrl();
		if (activeNavItem) {
			this.secondaryNavService.setActiveListItem(activeNavItem.id);
		}

		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				const activeNavItem = this.secondaryNavService.getActiveListItemFromUrl();
				if (activeNavItem) {
					this.secondaryNavService.setActiveListItem(activeNavItem.id);
				}
			});
	}

	/**
	 * Dynamically updates the secondary nav list items based on the route
	 * @param navListItems - SecondaryNavList representation of secondary nav buttons
	 */
	public getDynamicRoutes(navListItems: SecondaryNavList): string {
		if (navListItems.keyInParams) {
			return `${navListItems.feature}/${navListItems.key}/${navListItems.path}`;
		}
		return `${navListItems.feature}/${navListItems.path}`;
	}

	/**
	 * Handles the click event for secondary nav buttons to prevent full page reloads
	 * @param event - MouseEvent from the button click
	 */
	public handleNavButtonClick(event: MouseEvent): void {
		event.preventDefault();
	}

	/**
	 * updates data for button on secondary nav menu
	 * trigger progress indicator in the button
	 */
	public navButtonClick(): void {
		this.secondaryNavService.setSecNavButtonData({
			...this.secondaryNavService.secNavButtonData(),
			showProgressIndicator: true
		});
	}
}
